import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Column, Row } from 'react-table'
import Table from '../../../global/components/table/Table'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import LocationFilter from '../../../global/components/table/helpers/filters/LocationFilter'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import CheckboxFilter from '../../../global/components/table/helpers/filters/CheckboxFilter'
import { fetchData } from '../../../global/utils/fetch'
import { updateReworkCase } from '../../api'
import WorkOrderDetailModal from '../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../../../work-orders/components/context/context'

interface IReworkTableData {
  reworkOrderObjectId: string
  workOrderId: number | null
  date: Date | null
  technicianName: string
  locationName: string
  summary: string
  qualifyingDefect: boolean
  caseObjectId: string | null
  ogWorkOrderId: number | null
  ogOrderObjectId: string | null
  investigationStatus: string | null
  caseTechnicianObjectId: string | null
}

function ReworksTable() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { queryOrderData, clearData } = useContext(WoDetailContext)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [tableState, setTableState] = useState({
    data: [] as IReworkTableData[],
    isLoading: false,
    pageSize: 100,
    offSet: 0,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [locationColumnFilter, setLocationColumnFilter] = useState('')
  const [investigationStatusFilter, setInvestigationStatusFilter] = useState('')
  const [qualifyingDefectChecked, setQualifyingDefectChecked] = useState(false)

  const investigationStatusItems = [
    { key: 'n_a', value: 'N/A' },
    { key: 'under_investigation', value: 'In Progress' },
    { key: 'completed', value: 'Completed' },
  ]

  const endpoint = `${process.env.REACT_APP_ORIGIN}/incident-reports/reworks?`

  function retrieveDataWrapper() {
    retrieveData(setTableState, {
      endpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      columnFilters,
    })
  }

  useEffect(retrieveDataWrapper, [
    columnFilters,
    tableState.pageSize,
    tableState.offSet,
  ])

  useEffect(() => {
    if (id && !detailsModalOpen) {
      setDetailsModalOpen(true)
      queryOrderData(id)
    }
  }, [id])

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  async function handleRowClick(row: Row) {
    const original: any = row.original
    if (original.reworkOrderObjectId) {
      setDetailsModalOpen(true)
      navigate(`/quality-control/reworks/rework/${original.reworkOrderObjectId}`)
      await queryOrderData(original.reworkOrderObjectId)
    }
  }

  function handleDetailsClosed() {
    setDetailsModalOpen(false)
    navigate('/quality-control/reworks')
    clearData()
    retrieveDataWrapper()
  }

  async function handleQualifyingDefectToggle(
    e: React.MouseEvent,
    caseObjectId: string,
    caseTechnicianObjectId: string | null,
    currentValue: boolean,
  ) {
    e.stopPropagation()
    const newValue = !currentValue

    // Optimistically update local state to avoid full table reload
    setTableState((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item.caseObjectId === caseObjectId
          ? { ...item, qualifyingDefect: newValue }
          : item,
      ),
    }))

    try {
      const request = updateReworkCase(caseObjectId, caseTechnicianObjectId, {
        isTechnicianFault: newValue,
      })
      await fetchData(request)
    } catch (error) {
      console.error('Failed to update qualifying defect:', error)
      // Revert on failure
      setTableState((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item.caseObjectId === caseObjectId
            ? { ...item, qualifyingDefect: currentValue }
            : item,
        ),
      }))
    }
  }

  const columns: Column<any>[] = [
    {
      Header: 'Work Order #',
      accessor: (data: IReworkTableData) =>
        data.workOrderId?.toString() || '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ workOrderId: val }),
      }),
    },
    {
      Header: 'Date',
      accessor: (data: IReworkTableData) => {
        const date = data.date
        return date ? formatDateYyyyMmDd(new Date(date)) : ''
      },
      Filter: DateFilter({
        value: dateFromFilter,
        onChange: (val: string) => {
          setDateFromFilter(val)
          onColumnFilterUpdate({ dateFrom: val })
        },
      }),
    },
    {
      Header: 'Technician Name',
      accessor: (data: IReworkTableData) => data.technicianName || '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ technicianName: val }),
      }),
    },
    {
      Header: 'Location',
      accessor: (data: IReworkTableData) => data.locationName || '',
      Filter: LocationFilter({
        value: locationColumnFilter,
        onChange: (val: string) => {
          setLocationColumnFilter(val)
          onColumnFilterUpdate({ locationName: val })
        },
      }),
    },
    {
      Header: 'Summary',
      accessor: (data: IReworkTableData) => data.summary || '',
      Filter: () => null,
    },
    {
      Header: 'Qualifying Defect',
      accessor: 'qualifyingDefect',
      Filter: CheckboxFilter({
        checked: qualifyingDefectChecked,
        onChange: (checked: boolean) => {
          setQualifyingDefectChecked(checked)
          onColumnFilterUpdate({ qualifyingDefect: checked ? '1' : '' })
        },
      }),
      Cell: ({ row: { original } }: { row: { original: IReworkTableData } }) => {
        const { caseObjectId, caseTechnicianObjectId, qualifyingDefect } = original
        return (
          <input
            type='checkbox'
            checked={qualifyingDefect}
            disabled={!caseObjectId}
            onChange={() => {}}
            onClick={(e) => {
              if (caseObjectId) {
                handleQualifyingDefectToggle(e, caseObjectId, caseTechnicianObjectId, qualifyingDefect)
              }
            }}
            title={
              !caseObjectId
                ? 'No case exists for the original order'
                : ''
            }
          />
        )
      },
    },
    {
      Header: 'Investigation Status',
      accessor: 'investigationStatus',
      Filter: DropdownFilter({
        value: investigationStatusFilter,
        onChange: (val: string) => {
          setInvestigationStatusFilter(val)
          onColumnFilterUpdate({ investigationStatus: val })
        },
        items: investigationStatusItems,
      }),
      Cell: ({ row: { original } }: { row: { original: IReworkTableData } }) => {
        const { caseObjectId, investigationStatus } = original
        if (!caseObjectId) return <span>N/A</span>
        if (investigationStatus === 'completed') return <span>Completed</span>
        if (investigationStatus === 'under_investigation') return <span>In Progress</span>
        return <span>N/A</span>
      },
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleDetailsClosed}
        isOpen={detailsModalOpen}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={handleRowClick}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default ReworksTable
